/**
 * Isolated route shell for Cost Analysis — lazy-loaded from main.tsx.
 * Keeps costing embed failures inside this tab (error boundary + suspense).
 */

import React, { Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { ErrorBoundary, lazyWithRetry } from '@tracekit/core';

const CostAnalysisPage = lazyWithRetry(() => import('./CostAnalysisPage'));

function CostAnalysisFallback() {
  return (
    <div className="flex h-full items-center justify-center gap-2 bg-white text-sm text-gray-500">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading Cost Analysis…
    </div>
  );
}

function CostAnalysisErrorFallback() {
  return (
    <div className="flex h-full flex-col items-center justify-center bg-white p-8 text-center">
      <h2 className="text-lg font-semibold text-amber-800">
        Cost Analysis is temporarily unavailable
      </h2>
      <p className="mt-2 max-w-md text-sm text-gray-600">
        The costing dashboard could not be loaded. Other GovEarner tabs continue to work normally —
        try again in a moment or contact support if this persists.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-4 rounded-lg bg-sky-700 px-4 py-2 text-sm text-white hover:bg-sky-800"
      >
        Reload tab
      </button>
    </div>
  );
}

export function CostAnalysisRoute() {
  // Outer wrapper owns height (h-full, matching the flex-sized <main> ancestor in
  // AppShell — NOT a viewport-relative calc, which double-counted <main>'s own
  // padding and pushed content past the visible area) and an explicit opaque
  // background. This guarantees the tab is never transparent/unstyled — the
  // previous route's content can never show or flash through — for every state
  // (loading, error, or loaded), regardless of what nav item was active before.
  return (
    <div className="h-full bg-white" data-testid="cost-analysis-route">
      <ErrorBoundary fallback={<CostAnalysisErrorFallback />}>
        <Suspense fallback={<CostAnalysisFallback />}>
          <CostAnalysisPage />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

export default CostAnalysisRoute;
